import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  CASE_STATUS_LABELS, CASE_STATUS_OPTIONS, caseStatusBadge, initials, severityBadge,
} from '../../../core/critical-case/critical-case.labels';
import {
  CriticalCaseItem, CriticalCaseSeverity, CriticalCaseStatusCategory,
} from '../../../core/critical-case/critical-case.models';
import { CriticalCaseService } from '../../../core/critical-case/critical-case.service';
import { PageHeader } from '../../../shared/page-header/page-header';
import { Pagination } from '../../../shared/pagination/pagination';

type Tab = 'colaboradores' | 'clientes';

/**
 * Lista dos casos críticos do GBT Admin. Todo o estado do filtro vive no endereço
 * (?tipo=&q=&estado=&gravidade=&pagina=): partilhar o link ou voltar atrás repõe a lista.
 */
@Component({
  selector: 'app-critical-case-list',
  imports: [RouterLink, FormsModule, DatePipe, PageHeader, Pagination],
  templateUrl: './critical-case-list.html',
})
export class CriticalCaseList implements OnInit {
  private readonly service = inject(CriticalCaseService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly statusOptions = CASE_STATUS_OPTIONS;
  protected readonly statusLabels = CASE_STATUS_LABELS;
  protected readonly statusBadge = caseStatusBadge;
  protected readonly severityBadge = severityBadge;
  protected readonly initials = initials;

  protected readonly items = signal<CriticalCaseItem[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly total = signal(0);

  protected readonly pageSize = 20;
  protected readonly page = signal(1);
  protected readonly tab = signal<Tab>('colaboradores');
  protected readonly status = signal<CriticalCaseStatusCategory | null>(null);
  protected readonly severityId = signal<number | null>(null);
  /** Vazio quando o pedido falha: o filtro some, a lista continua a funcionar. */
  protected readonly severities = signal<CriticalCaseSeverity[]>([]);
  protected q = '';

  private request: Subscription | null = null;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.service.severities().subscribe({
      next: (list) => this.severities.set(list),
      error: () => this.severities.set([]),
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.tab.set(params.get('tipo') === 'clientes' ? 'clientes' : 'colaboradores');
      this.q = params.get('q') ?? '';
      const estado = params.get('estado');
      this.status.set(CASE_STATUS_OPTIONS.some((o) => o.value === estado)
        ? (estado as CriticalCaseStatusCategory) : null);
      const gravidade = Number(params.get('gravidade'));
      this.severityId.set(Number.isInteger(gravidade) && gravidade > 0 ? gravidade : null);
      const pagina = Number(params.get('pagina'));
      this.page.set(Number.isInteger(pagina) && pagina > 0 ? pagina : 1);
      this.fetch();
    });
  }

  protected selectTab(tab: Tab): void {
    this.navigate({ tipo: tab === 'clientes' ? 'clientes' : null, pagina: null });
  }

  protected onSearch(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.navigate({ q: this.q.trim() || null, pagina: null }), 350);
  }

  protected onStatus(value: CriticalCaseStatusCategory | null): void {
    this.navigate({ estado: value, pagina: null });
  }

  protected onSeverity(value: number | null): void {
    this.navigate({ gravidade: value, pagina: null });
  }

  protected onPage(page: number): void {
    this.navigate({ pagina: page > 1 ? page : null });
  }

  protected clearFilters(): void {
    this.navigate({ q: null, estado: null, gravidade: null, pagina: null });
  }

  protected get filtered(): boolean {
    return !!(this.q || this.status() || this.severityId());
  }

  private navigate(changes: Record<string, string | number | null>): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: changes,
      queryParamsHandling: 'merge',
    });
  }

  private fetch(): void {
    this.request?.unsubscribe();
    this.loading.set(true);
    this.error.set(null);
    this.request = this.service.list({
      search: this.q.trim(),
      statusCategory: this.status(),
      severityId: this.severityId(),
      hasClient: this.tab() === 'clientes',
      page: this.page() - 1,
      size: this.pageSize,
    }).subscribe({
      next: (res) => {
        this.items.set(res.content);
        this.total.set(res.totalItems);
        this.loading.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.items.set([]);
        this.total.set(0);
        this.error.set((err.error as { message?: string } | null)?.message
          ?? 'Não foi possível carregar os casos críticos.');
        this.loading.set(false);
      },
    });
  }
}
